import React, { useCallback } from 'react';
import styled from 'styled-components';
import media from 'styled-media-query';
import { useNavigate } from 'react-router-dom';

import { Button } from '../atoms/Button';
import { H1 } from '../atoms/Typography';

import LessonImg from '../../static/image/spot_lesson_img.png';
import { SPOT_LESSON_TIME } from '../../const/SpotLesson';
import { useCommonModal } from '../../redux/common_modal/useCommonModal';
import { FunctionType, PermissionType } from '../../const/UserPermission';
import { TicketFragment } from '../../gen/graphql';

interface Props {
  tickets: readonly TicketFragment[];
}

export const SpotLessonBanner: React.FC<Props> = (props): JSX.Element => {
  const navigate = useNavigate();
  const { openModal } = useCommonModal();

  const handleClickReserveButton = useCallback(() => {
    if (openModal(FunctionType.SpotLessonReserve, PermissionType.Create)) return;
    navigate('/spot_lessons/reserve');
  }, [openModal, navigate]);

  const hasTickets = props.tickets.length >= 1;

  return (
    <Container>
      <H1 color={'DARK'}>現役エンジニアとのオンラインマンツーマンレッスン</H1>
      <p>
        「レッスン」は、現役エンジニアと{SPOT_LESSON_TIME}
        分間、マンツーマンでレッスンできるサービスです。
        <br />
        Q&Aで解決できない悩みや言語化できない疑問をインストラクターに相談してみましょう。
      </p>
      <HelpLink href="https://tayori.com/q/samurai-terakoya/category/132766/" target="_blank">
        レッスンとは？
      </HelpLink>
      <HelpLink href="https://tayori.com/q/samurai-terakoya/detail/874788/" target="_blank">
        レッスンご予約時のお願い
      </HelpLink>
      <ButtonContainer>
        <Button disabled={!hasTickets} onClick={handleClickReserveButton}>
          レッスンを予約する
        </Button>
      </ButtonContainer>

      {!hasTickets && (
        <LessonTicketRequiredNotice>レッスンチケットが付与されるまで予約できません。</LessonTicketRequiredNotice>
      )}
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
  padding: 1.6rem 15.875rem 3rem 2rem;
  box-sizing: border-box;
  background-color: #e5ecfd;
  color: rgba(0, 0, 0, 0.87);
  font-size: 1rem;
  line-height: 1.8;
  position: relative;

  span {
    font-size: 1rem;
    font-weight: 400;
  }

  &:after {
    content: '';
    display: block;
    max-width: 440px;
    ${media.lessThan('medium')`
      max-width: 100%; 
    `}
    width: 30%;
    height: 0;
    padding-top: calc(300% / 2);
    background: url(${LessonImg}) bottom center / contain no-repeat;
    position: absolute;
    bottom: 1rem;
    right: 2.25rem;
    pointer-events: none;
  }

  ${media.lessThan('medium')`
    margin-bottom: 1.5rem;
    padding: 2rem 1rem;
    box-sizing: border-box;
    font-size: 0.75rem;
    line-height: 1.5;

    span {
      font-size: 0.875rem;
    }

    &:after {
      bottom: 8.5rem;
      height: 180px;
      width: 300px;
      padding-top: 0;
      transform: translateX(-50%);
      left: 50%;
    }
  `}

  ${media.lessThan('small')`
    br {
      display: none;
    }
  `}
`;

const HelpLink = styled.a`
  padding-right: 1rem;
  color: #eb0000;
  font-size: 0.8rem;

  &:hover {
    text-decoration: underline;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-top: 1rem;

  ${media.lessThan('medium')`
    flex-direction: column;
    justify-content: center;
    gap: .625rem;
    margin-top: 11.25rem;
  `}
`;

const LessonTicketRequiredNotice = styled.div`
  padding-right: 1rem;
  color: #eb0000;
  font-size: 0.8rem;
  margin-top: 0.5rem;

  ${media.lessThan('medium')`
    witdth: 100%;
    text-align: center;
  `}
`;
